import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  exportUrl, MissingAccao, MissingFilters, MissingList, MissingRow, replenishmentMissingApi,
} from '../services/replenishmentMissingApi';
import { fmtInt, PHYSICAL_STORES, storeLabel } from '../utils/replenishment';

const PAGE_SIZE = 100;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const FILTER_KEYS = ['start', 'end', 'stores', 'causas', 'cod', 'accao', 'search', 't_min_days', 'ordering'] as const;
const DEFAULT_CAUSAS = 'catalogacao,distribuicao,ruptura';
const WITH_QUANTITY = `${DEFAULT_CAUSAS},quantidade`;

const ACCAO_META: Record<MissingAccao, { label: string; badge: string; help: string }> = {
  repor: { label: 'Repor nas lojas em falta', badge: 'bg-success',
    help: 'D2 e o T000 continua com stock suficiente depois de enviar às lojas em falta' },
  proteger_t000: { label: 'Não repor — proteger T000', badge: 'bg-dark',
    help: 'Enviar às lojas deixava o T000 abaixo do mínimo de dias de stock: alertar compras' },
  sem_stock_t000: { label: 'Sem stock no T000 — compras', badge: 'bg-danger', help: 'Não há stock no T000 para repor' },
  avaliar_d2: { label: 'D1 — avaliar passar a D2', badge: 'bg-warning text-dark',
    help: 'Catalogado só online, mas falhou em loja e o T000 aguenta: ver a recorrência (28 dias)' },
  sem_classificacao: { label: 'Sem catalogação', badge: 'bg-light text-dark border', help: 'SKU fora da catalogação' },
};
const ACCAO_ORDER = Object.keys(ACCAO_META) as MissingAccao[];

const CAUSA_LABEL: Record<string, string> = {
  catalogacao: 'só no T000', distribuicao: 'noutra loja', ruptura: 'sem stock', quantidade: 'quantidade',
};

const ORDERINGS: Array<[string, string]> = [
  ['impacto', 'Impacto no hit rate'], ['falhas', 'Encomendas falhadas'], ['recorrencia', 'Recorrência (28 dias)'],
  ['dias_t', 'Dias de stock T000 (menos primeiro)'],
];

const lisbonToday = (): string => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Lisbon' });
const shiftDate = (iso: string, days: number): string => {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};
const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const weekday = (iso: string): string => WEEKDAYS[new Date(`${iso}T12:00:00Z`).getUTCDay()];
const fmtDateFull = (iso: string): string => iso.split('-').reverse().join('/');
const fmtDec = (v: number | null | undefined, digits = 1): string =>
  v === null || v === undefined ? '-' : v.toLocaleString('pt-PT', { minimumFractionDigits: digits, maximumFractionDigits: digits });

const daysTone = (days: number | null, min: number): string => {
  if (days === null) return 'text-muted';
  if (days < min) return 'text-danger fw-semibold';
  if (days < 2 * min) return 'text-warning';
  return 'text-success';
};

const STORE_COLS: Array<[string, keyof MissingRow]> = [
  ['A000', 'stock_a000'], ['B000', 'stock_b000'], ['C000', 'stock_c000'],
  ['D000', 'stock_d000'], ['E000', 'stock_e000'], ['F000', 'stock_f000'],
];
const fmtTime = (iso: string | null): string => (iso ? iso.slice(11, 16) : '-');

// Stock da loja no snapshot da 1.ª encomenda falhada; a negrito/vermelho as lojas onde falhou
const StoreStockCell: React.FC<{ row: MissingRow; store: string; field: keyof MissingRow }> = ({ row, store, field }) => {
  const v = row[field] as number | null;
  const failed = row.lojas_em_falta.split(',').includes(store);
  const cls = failed ? 'fw-bold text-danger' : (v ?? 0) > 0 ? '' : 'text-muted';
  return <td className={`text-end ${cls}`} title={failed ? 'Loja onde a encomenda falhou' : ''}>{v === null ? '-' : fmtInt(v)}</td>;
};

const T000Cell: React.FC<{ row: MissingRow; min: number }> = ({ row, min }) => {
  const sales = (
    <div className="small text-muted" title="Vendas dos últimos 30 dias até ontem: online (T000) + lojas">
      vendas 30d: {fmtInt(row.v30_total)} ({fmtInt(row.v30_online)} + {fmtInt(row.v30_lojas)})
    </div>
  );
  if (!row.stock_t_actual || row.stock_t_actual <= 0) {
    return (
      <div className="text-nowrap">
        <span className="text-danger fw-semibold">0 un.</span>
        {sales}
        {row.compra_t ? <div className="small text-muted">+{fmtInt(row.compra_t)} em compra</div> : null}
      </div>
    );
  }
  return (
    <div className="text-nowrap">
      {fmtInt(row.stock_t_actual)} un. · <span className={daysTone(row.dias_t_actual, min)}>
        {row.dias_t_actual === null ? 'sem vendas' : `${fmtDec(row.dias_t_actual)} dias`}
      </span>
      <div className="small">
        depois de enviar {row.un_envio}: <span className={daysTone(row.dias_t_apos, min)}>
          {row.dias_t_apos === null ? `${fmtInt(Math.max(row.stock_t_apos ?? 0, 0))} un.` : `${fmtDec(row.dias_t_apos)} dias`}
        </span>
      </div>
      {sales}
      {row.compra_t ? <div className="small text-muted">+{fmtInt(row.compra_t)} em compra</div> : null}
    </div>
  );
};

const CodBadge: React.FC<{ cod: string | null }> = ({ cod }) => (
  cod ? <span className={`badge ${cod === 'D2' ? 'bg-primary' : 'bg-secondary'}`}>{cod}</span> : <span className="text-muted">-</span>
);

const ReplenishmentMissingPage: React.FC = () => {
  const yesterday = useMemo(() => shiftDate(lisbonToday(), -1), []);
  const [params, setParams] = useSearchParams();

  const filters: MissingFilters = useMemo(() => {
    const f: Record<string, string> = {};
    FILTER_KEYS.forEach((k) => {
      const v = params.get(k);
      if (v && (!['start', 'end'].includes(k) || ISO_DATE.test(v))) f[k] = v;
    });
    if (!f.start && !f.end) { f.start = yesterday; f.end = yesterday; }
    if (!f.end) f.end = f.start;
    if (!f.start) f.start = f.end;
    return f as MissingFilters;
  }, [params, yesterday]);
  const page = Math.max(0, Number(params.get('page')) || 0);
  const singleDay = filters.start === filters.end;
  const minDays = Number(filters.t_min_days) || 10;

  const patchParams = useCallback((changes: Record<string, string>, push = false) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(changes).forEach(([k, v]) => (v ? next.set(k, v) : next.delete(k)));
      if (!('page' in changes)) next.delete('page');
      return next;
    }, { replace: !push });
  }, [setParams]);

  const [data, setData] = useState<MissingList | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState(filters.search || '');

  const filtersKey = JSON.stringify(filters);
  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    replenishmentMissingApi.list(filters, PAGE_SIZE, page * PAGE_SIZE)
      .then((d) => alive && setData(d))
      .catch((e) => alive && setError(e?.response?.data?.error || 'Erro ao carregar os produtos em falta'))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey, page]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (searchText !== (filters.search || '')) patchParams({ search: searchText });
    }, 400);
    return () => clearTimeout(t);
  }, [searchText]); // eslint-disable-line react-hooks/exhaustive-deps

  const moveDay = (delta: number) => {
    const d = shiftDate(filters.start!, delta);
    patchParams({ start: d, end: d }, true);
  };

  const selectedAccoes = useMemo(() => (filters.accao ? filters.accao.split(',') : []), [filters.accao]);
  const toggleAccao = (a: string) => {
    const next = selectedAccoes.includes(a) ? selectedAccoes.filter((x) => x !== a) : [...selectedAccoes, a];
    patchParams({ accao: next.join(',') });
  };
  const selectedStores = useMemo(() => (filters.stores ? filters.stores.split(',') : PHYSICAL_STORES), [filters.stores]);
  const toggleStore = (s: string) => {
    const next = selectedStores.includes(s) ? selectedStores.filter((x) => x !== s) : [...selectedStores, s];
    patchParams({ stores: next.length === PHYSICAL_STORES.length || next.length === 0 ? '' : PHYSICAL_STORES.filter((x) => next.includes(x)).join(',') });
  };

  const rows = data?.results || [];
  const totalPages = data ? Math.max(1, Math.ceil(data.count / PAGE_SIZE)) : 1;
  const totals = useMemo(() => {
    const days = data?.days || [];
    const sum = (k: 'enc' | 'hits' | 'enc_so_ausencia' | 'enc_so_este_repor') => days.reduce((a, d) => a + d[k], 0);
    const enc = sum('enc');
    return {
      enc, hits: sum('hits'), soAusencia: sum('enc_so_ausencia'), repor: sum('enc_so_este_repor'),
      hr: enc ? (100 * sum('hits')) / enc : null,
      hrRepor: enc ? (100 * (sum('hits') + sum('enc_so_este_repor'))) / enc : null,
      hrAusencia: enc ? (100 * (sum('hits') + sum('enc_so_ausencia'))) / enc : null,
    };
  }, [data]);

  return (
    <div className="pb-5">
      <div className="d-flex flex-wrap justify-content-between align-items-end mb-2 gap-2">
        <div>
          <h2 className="mb-0">🔍 Produtos em falta nas lojas</h2>
          <div className="text-muted small">
            SKUs que não estavam na loja e fizeram falhar encomendas online com levantamento, por dia. O impacto conta as
            encomendas que falharam <strong>só</strong> por este SKU (seriam hit se estivesse na loja). Stock das lojas A–F
            e do T000 no momento da 1.ª encomenda falhada. Dias de stock do T000 = stock de hoje ÷ vendas médias dos últimos
            30 dias (online + lojas): só se sugere repor se o T000 fica com pelo menos {minDays} dias depois de enviar às
            lojas em falta.
          </div>
        </div>
        <div className="text-muted small text-end">
          {data?.snapshot_actual && <>Catalogação de {fmtDateFull(data.snapshot_actual)}</>}
        </div>
      </div>

      {/* Período e lojas */}
      <div className="card mb-3">
        <div className="card-body d-flex flex-wrap gap-3 align-items-end">
          <div>
            <label className="small text-muted d-block">Dia</label>
            <div className="input-group input-group-sm">
              <button type="button" className="btn btn-outline-secondary" aria-label="Dia anterior" onClick={() => moveDay(-1)}>‹</button>
              <input type="date" aria-label="Início" className="form-control form-control-sm" value={filters.start}
                onChange={(e) => e.target.value && patchParams({ start: e.target.value, end: singleDay ? e.target.value : filters.end! })} />
              <input type="date" aria-label="Fim" className="form-control form-control-sm" value={filters.end}
                onChange={(e) => e.target.value && patchParams({ end: e.target.value })} />
              <button type="button" className="btn btn-outline-secondary" aria-label="Dia seguinte" disabled={filters.start! >= yesterday}
                onClick={() => moveDay(1)}>›</button>
            </div>
            <div className="small mt-1">
              <button type="button" className="btn btn-link btn-sm p-0 me-2" onClick={() => patchParams({ start: '', end: '' })}>ontem</button>
              <button type="button" className="btn btn-link btn-sm p-0" onClick={() => patchParams({ start: shiftDate(yesterday, -6), end: yesterday })}>7 dias</button>
            </div>
          </div>
          <div>
            <label className="small text-muted d-block">Lojas</label>
            <div className="btn-group btn-group-sm">
              {PHYSICAL_STORES.map((s) => (
                <button key={s} type="button" aria-pressed={selectedStores.includes(s)}
                  className={`btn ${selectedStores.includes(s) ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => toggleStore(s)}>{storeLabel(s)}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="mi-cod">D1/D2 (hoje)</label>
            <select id="mi-cod" className="form-select form-select-sm" value={filters.cod || ''}
              onChange={(e) => patchParams({ cod: e.target.value })}>
              <option value="">Todos</option><option value="D2">D2</option><option value="D1">D1</option>
              <option value="none">Sem catalogação</option>
            </select>
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="mi-tmin">Mínimo T000 (dias)</label>
            <input id="mi-tmin" type="number" min={0} max={90} className="form-control form-control-sm" style={{ width: 90 }}
              value={filters.t_min_days || '10'} onChange={(e) => patchParams({ t_min_days: e.target.value === '10' ? '' : e.target.value })} />
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="mi-search">Pesquisa</label>
            <input id="mi-search" type="search" className="form-control form-control-sm" placeholder="SKU ou nome"
              value={searchText} onChange={(e) => setSearchText(e.target.value)} />
          </div>
          <div>
            <label className="small text-muted d-block" htmlFor="mi-order">Ordenar</label>
            <select id="mi-order" className="form-select form-select-sm" value={filters.ordering || 'impacto'}
              onChange={(e) => patchParams({ ordering: e.target.value === 'impacto' ? '' : e.target.value })}>
              {ORDERINGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div className="form-check">
            <input id="mi-qty" className="form-check-input" type="checkbox" checked={filters.causas === WITH_QUANTITY}
              onChange={(e) => patchParams({ causas: e.target.checked ? WITH_QUANTITY : '' })} />
            <label className="form-check-label small" htmlFor="mi-qty">Incluir quantidade insuficiente</label>
          </div>
          <div className="ms-auto d-flex gap-2 align-items-center">
            {loading && <div className="spinner-border spinner-border-sm text-primary" role="status" />}
            <button type="button" className="btn btn-sm btn-outline-secondary"
              onClick={() => { setSearchText(''); setParams({}, { replace: true }); }}>Limpar</button>
            <a className="btn btn-sm btn-success" href={exportUrl(filters)} download>⬇ Exportar CSV</a>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* KPIs */}
      {data && (
        <div className="row g-2 mb-3">
          <div className="col-6 col-md-3">
            <div className="card h-100"><div className="card-body py-2">
              <div className="text-muted small">Hit rate {singleDay ? `${weekday(data.start)} ${fmtDateFull(data.start)}` : 'do período'}</div>
              <div className="fs-4 fw-semibold" data-testid="kpi-hr">{fmtDec(totals.hr)}%</div>
              <div className="small text-muted">{fmtInt(totals.hits)} de {fmtInt(totals.enc)} encomendas</div>
            </div></div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card h-100"><div className="card-body py-2">
              <div className="text-muted small">Falharam só por produto ausente</div>
              <div className="fs-4 fw-semibold">{fmtInt(totals.soAusencia)}</div>
              <div className="small text-muted">máximo recuperável: {fmtDec(totals.hrAusencia)}%</div>
            </div></div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card h-100 border-success"><div className="card-body py-2">
              <div className="text-muted small">Hit rate se os "repor" estivessem na loja</div>
              <div className="fs-4 fw-semibold text-success" data-testid="kpi-hr-repor">{fmtDec(totals.hrRepor)}%</div>
              <div className="small text-muted">+{fmtInt(totals.repor)} encomendas, sem pôr o T000 em risco</div>
            </div></div>
          </div>
          <div className="col-6 col-md-3">
            <div className="card h-100 border-dark"><div className="card-body py-2">
              <div className="text-muted small">A proteger no T000 / sem stock</div>
              <div className="fs-4 fw-semibold">{fmtInt(data.counts_accao.proteger_t000 || 0)} / {fmtInt(data.counts_accao.sem_stock_t000 || 0)}</div>
              <div className="small text-muted">SKUs para compras</div>
            </div></div>
          </div>
        </div>
      )}

      {/* Por dia (período) */}
      {data && !singleDay && data.days.length > 0 && (
        <div className="card mb-3">
          <div className="card-body">
            <h6 className="mb-2">Por dia</h6>
            <div className="table-responsive">
              <table className="table table-sm table-hover small mb-0 w-auto">
                <thead className="table-light">
                  <tr><th>Dia</th><th className="text-end">Encomendas</th><th className="text-end">Hit rate</th>
                    <th className="text-end">Só por ausência</th><th className="text-end">Hit rate se "repor"</th></tr>
                </thead>
                <tbody>
                  {data.days.map((d) => (
                    <tr key={d.order_date} style={{ cursor: 'pointer' }} onClick={() => patchParams({ start: d.order_date, end: d.order_date }, true)}>
                      <td className="text-nowrap">{weekday(d.order_date)} {fmtDateFull(d.order_date)}</td>
                      <td className="text-end">{d.enc}</td>
                      <td className="text-end">{fmtDec(d.hit_rate)}%</td>
                      <td className="text-end">{d.enc_so_ausencia}</td>
                      <td className="text-end text-success">{fmtDec(d.hit_rate_se_repor)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Acções */}
      {data && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          {ACCAO_ORDER.map((a) => (
            <button key={a} type="button" aria-pressed={selectedAccoes.includes(a)} title={ACCAO_META[a].help}
              className={`btn btn-sm ${selectedAccoes.includes(a) ? 'btn-primary' : 'btn-outline-secondary'}`}
              onClick={() => toggleAccao(a)}>
              {ACCAO_META[a].label} <span className="badge bg-secondary ms-1">{data.counts_accao[a] || 0}</span>
            </button>
          ))}
        </div>
      )}

      {/* Lista */}
      {data && (
        <div className="card">
          <div className="card-body">
            <div className="d-flex justify-content-between mb-2">
              <h6 className="mb-0">{fmtInt(data.count)} produtos{singleDay ? '' : ' × dia'}</h6>
              <div className="small">
                <button type="button" className="btn btn-sm btn-outline-secondary me-1" disabled={page === 0}
                  onClick={() => patchParams({ page: String(page - 1 || '') }, true)}>‹</button>
                Página {page + 1} / {totalPages}
                <button type="button" className="btn btn-sm btn-outline-secondary ms-1" disabled={page + 1 >= totalPages}
                  onClick={() => patchParams({ page: String(page + 1) }, true)}>›</button>
              </div>
            </div>
            {rows.length === 0 ? <div className="text-muted">Sem produtos em falta para estes filtros.</div> : (
              <div className="table-responsive">
                <table className="table table-sm table-hover small align-middle">
                  <thead className="table-light">
                    <tr>
                      {!singleDay && <th>Dia</th>}
                      <th>Acção</th><th>SKU</th>
                      <th className="text-end" title="Encomendas que falharam só por este SKU (pontos de hit rate do dia)">Impacto</th>
                      <th className="text-end">Enc. falhadas</th><th>Lojas em falta</th>
                      <th title="Catalogação no dia → hoje">D1/D2</th>
                      {STORE_COLS.map(([s]) => (
                        <th key={s} className="text-end" title="Stock disponível no snapshot da 1.ª encomenda falhada">{s.slice(0, 1)}</th>
                      ))}
                      <th className="text-end" title="Stock T000 no snapshot da 1.ª encomenda falhada">T000 no momento</th>
                      <th>T000 hoje (dias com vendas 30d)</th>
                      <th className="text-end" title="Dias com falha por ausência nos últimos 28 dias">Recorrência</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={`${r.order_date}|${r.sku}`}>
                        {!singleDay && <td className="text-nowrap">{weekday(r.order_date)} {fmtDateFull(r.order_date).slice(0, 5)}</td>}
                        <td><span className={`badge ${ACCAO_META[r.accao].badge}`} title={ACCAO_META[r.accao].help}>{ACCAO_META[r.accao].label}</span></td>
                        <td>
                          <span className="fw-semibold">{r.sku}</span>
                          <div className="text-muted" style={{ maxWidth: 320 }}>{r.nome || '-'}</div>
                          <div className="text-muted">{r.cat2 || ''}{r.estado ? ` · ${r.estado}` : ''}</div>
                        </td>
                        <td className="text-end text-nowrap">
                          <strong>{r.enc_so_este}</strong>
                          <div className="text-muted">{fmtDec(r.pp_hit_rate, 2)} pp</div>
                        </td>
                        <td className="text-end">{r.enc_falhadas}</td>
                        <td>
                          {r.lojas_em_falta.split(',').map(storeLabel).join(', ')}
                          <div className="text-muted">{r.causas.split(',').map((c) => CAUSA_LABEL[c] || c).join(', ')}</div>
                          <div className="text-muted" title={`Snapshot de stock: ${r.snapshot_stock || '-'}`}>1.ª às {fmtTime(r.primeira_encomenda)}</div>
                        </td>
                        <td className="text-nowrap" title={r.motivo_v3 || ''}>
                          <CodBadge cod={r.cod_dia} />{r.cod_dia !== r.cod_actual && <> → <CodBadge cod={r.cod_actual} /></>}
                        </td>
                        {STORE_COLS.map(([s, f]) => <StoreStockCell key={s} row={r} store={s} field={f} />)}
                        <td className="text-end">{r.stock_t000_momento === null ? '-' : fmtInt(r.stock_t000_momento)}</td>
                        <td><T000Cell row={r} min={minDays} /></td>
                        <td className="text-end">{r.dias_falha_28d ?? '-'}<span className="text-muted"> d</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReplenishmentMissingPage;
